import { GameCategory, type MatchResult } from '@sportspredict/shared';

/** Local view model assembled from existing match and matches/live responses. */
export type TeamMatchResult = MatchResult & {
	periodScores?: { period: number; home: number | null; away: number | null }[];
};

export type TeamMatchSport = 'nfl' | 'epl';
export type TeamMatchPhase =
	| 'upcoming'
	| 'live'
	| 'halftime'
	| 'suspended'
	| 'settling'
	| 'settled'
	| 'cancelled'
	| 'unknown';

export function getTeamMatchSport(categoryId?: string | null): TeamMatchSport | undefined {
	const isNfl = categoryId === GameCategory.NFL;
	const isEpl = categoryId === GameCategory.EPL;
	return isNfl ? 'nfl' : isEpl ? 'epl' : undefined;
}

/** Display lifecycle only: never use this to authorize forecasting or settle markets. */
export function getTeamMatchPhase(
	match: {
		result?: MatchResult | null;
		opening_time?: Date | string | null;
		closing_time?: Date | string | null;
	},
	nowMs = Date.now()
): TeamMatchPhase {
	switch (match.result?.fixtureStatus) {
		case 'completed':
			return 'settled';
		case 'cancelled':
			return 'cancelled';
		case 'suspended':
			return 'suspended';
		case 'half':
			return 'halftime';
		case 'unplayed':
		case 'delayed':
			return 'upcoming';
		case 'live':
			return String(match.result?.period ?? '').toUpperCase() === 'HALF' ? 'halftime' : 'live';
	}
	if (match.result?.isLive) return 'live';
	const start = match.opening_time ? new Date(match.opening_time).getTime() : NaN;
	const end = match.closing_time ? new Date(match.closing_time).getTime() : NaN;
	if (start > nowMs) return 'upcoming';
	if (start <= nowMs && end > nowMs) return 'live';
	// A scheduled deadline alone cannot establish a final score or winner.
	return 'unknown';
}

export const isActiveTeamMatchPhase = (phase: TeamMatchPhase): boolean =>
	phase === 'live' || phase === 'halftime' || phase === 'suspended';

export function getTeamMatchPeriod(sport: TeamMatchSport, raw?: string | null): number | null {
	const value = String(raw ?? '')
		.trim()
		.toUpperCase();
	if (sport === 'epl') {
		if (value === '1' || value === '1H') return 1;
		if (value === '2' || value === '2H') return 2;
		return null;
	}
	const quarter = /^(?:Q)?([1-4])$/.exec(value);
	if (quarter) return Number(quarter[1]);
	const overtime = /^(\d*)OT$/.exec(value);
	if (overtime && Number(overtime[1] || 1) > 0) return 4 + Number(overtime[1] || 1);
	// Explicit successive period numbers are preserved; never infer OT from a tied score.
	return /^\d+$/.test(value) && Number(value) >= 5 ? Number(value) : null;
}

export interface TeamScoreColumn {
	key: string;
	label: string;
	active: boolean;
	home: number | null;
	away: number | null;
}

export function getTeamScoreColumns(
	sport: TeamMatchSport,
	result: TeamMatchResult | null | undefined,
	phase: TeamMatchPhase
): TeamScoreColumn[] {
	const periods = result?.periodScores ?? [];
	const current = getTeamMatchPeriod(sport, result?.period);
	const regulationCount = sport === 'nfl' ? 4 : 2;
	const playedThrough = phase === 'halftime' ? regulationCount / 2 : current;
	const recordedPeriods = periods.filter(
		(p) =>
			Number.isSafeInteger(p.period) &&
			p.period > 0 &&
			(p.home != null || p.away != null) &&
			(phase === 'settled' || playedThrough == null || p.period <= playedThrough)
	);
	const last =
		sport === 'nfl'
			? Math.max(regulationCount, current ?? 0, ...recordedPeriods.map((p) => p.period))
			: regulationCount;
	const scoreFor = (period: number, side: 'home' | 'away') => {
		if (
			phase === 'upcoming' ||
			((phase === 'live' || phase === 'halftime' || phase === 'suspended') &&
				playedThrough != null &&
				period > playedThrough)
		)
			return null;
		const value = periods.find((p) => p.period === period)?.[side];
		return value != null && Number.isFinite(value) ? value : null;
	};
	const column = (period: number): TeamScoreColumn => ({
		key: String(period),
		label:
			sport === 'epl'
				? `${period}H`
				: period <= 4
					? `Q${period}`
					: period === 5
						? 'OT'
						: `${period - 4}OT`,
		active: phase === 'live' && current === period,
		home: scoreFor(period, 'home'),
		away: scoreFor(period, 'away'),
	});
	if (sport === 'nfl' && last >= 6) {
		const regulation = (side: 'home' | 'away') => {
			const values = [1, 2, 3, 4].map((p) => scoreFor(p, side));
			return values.every((v): v is number => v != null)
				? values.reduce((sum, value) => sum + value, 0)
				: null;
		};
		return [
			{
				key: 'reg',
				label: 'REG',
				active: false,
				home: regulation('home'),
				away: regulation('away'),
			},
			...Array.from({ length: last - 4 }, (_, i) => column(i + 5)),
		];
	}
	return Array.from({ length: last }, (_, i) => column(i + 1));
}
